import React, { createContext, useContext, useState, useEffect } from 'react';
import { isAuthenticated, clearAuthToken, getUser, storeUser, setAuthToken } from '../utils/auth';
import { setJwtToken, clearJwtToken, isTokenExpired, refreshToken, getTimeUntilExpiration } from '../utils/jwtService';
import { setLogoutCallback } from '../utils/fetchWithAuth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Check if running on localhost for dev bypass.
//
// REACT_APP_DISABLE_LOCAL_DEV_LOGIN opts a local build out of the bypass. Without it the
// /login route is unreachable on a dev host — the auto-login authenticates before any route
// renders, so LoginRoute redirects to `/` and no browser test can exercise the OAuth callback
// landing there. That is why the 2026-08-29 redemption-loop regression had no e2e coverage.
// Build-time and opt-in, matching REACT_APP_MANUAL_QA_LOCAL_PREVIEW; it only ever removes an
// automatic grant of Admin, so it cannot widen access.
const isLocalDev = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
  process.env.REACT_APP_DISABLE_LOCAL_DEV_LOGIN !== 'true';

const createLocalDevJwt = (user) => {
  const encode = (value) => window.btoa(JSON.stringify(value))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  const nowSeconds = Math.floor(Date.now() / 1000);

  return [
    encode({ alg: 'none', typ: 'JWT' }),
    encode({
      sub: user.RowKey,
      email: user.Email,
      role: user.Role,
      tenantId: user.TenantId,
      exp: nowSeconds + (24 * 60 * 60)
    }),
    'local-dev-signature'
  ].join('.');
};

export const AuthProvider = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check authentication status on mount
    const checkAuth = () => {
      // LOCAL DEV BYPASS: Auto-login with dev user when running on localhost
      if (isLocalDev) {
        const devUser = {
          Email: 'dev@localhost',
          Name: 'Local Dev User',
          Role: 'Admin',  // Admin role for full access during development
          RowKey: 'dev-local-user',
          TenantId: 'c2hl',
          TenantName: 'C2HL',
          // The login response's platform flags (AuthFunctions.cs: level >= 4 / 5). A tenant
          // Admin alone does not clear the platform gates on /admin-settings and /find-user.
          IsPlatformAdmin: true,
          IsSuperAdmin: true
        };
        console.log('[Auth] LOCAL DEV MODE: Auto-login bypassing authentication');
        setAuthenticated(true);
        setUser(devUser);
        storeUser(devUser);
        setJwtToken(createLocalDevJwt(devUser));
        setLoading(false);
        return;
      }

      const isAuth = isAuthenticated();
      setAuthenticated(isAuth);

      if (isAuth) {
        // Load user profile
        const storedUser = getUser();
        setUser(storedUser);
      }

      setLoading(false);
    };

    checkAuth();
  }, []);

  // Track if a refresh is in progress to prevent multiple simultaneous refreshes
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Monitor JWT token expiration and auto-refresh when expiring
  useEffect(() => {
    if (!authenticated) {
      return; // Skip if not authenticated
    }

    // Check token expiration and attempt refresh if needed
    const checkTokenExpiration = async () => {
      const minutesRemaining = getTimeUntilExpiration();

      // Token is expiring within 5 minutes - attempt refresh
      if (isTokenExpired(5)) {
        if (isRefreshing) {
          console.log('[Auth] Token refresh already in progress, skipping');
          return;
        }

        console.log('[Auth] JWT token expiring soon (', minutesRemaining, 'min remaining), attempting refresh...');
        setIsRefreshing(true);

        try {
          const result = await refreshToken();

          if (result.success) {
            console.log('[Auth] Token refreshed successfully');
            // Update user info if returned from refresh
            if (result.user) {
              setUser(prevUser => ({ ...prevUser, ...result.user }));
              storeUser({ ...getUser(), ...result.user });
            }
          } else {
            console.warn('[Auth] Token refresh failed:', result.error);
            // Only logout if refresh fails and token is actually expired (not just expiring soon)
            if (isTokenExpired(0)) {
              console.log('[Auth] Token is fully expired and refresh failed, logging out');
              logout();
            }
          }
        } catch (error) {
          console.error('[Auth] Error during token refresh:', error);
          if (isTokenExpired(0)) {
            logout();
          }
        } finally {
          setIsRefreshing(false);
        }
      }
    };

    // Initial check
    checkTokenExpiration();

    // Set up interval to check every 60 seconds
    const interval = setInterval(checkTokenExpiration, 60000);

    return () => clearInterval(interval);
  }, [authenticated, isRefreshing]); // Re-run when authentication state changes

  // Register logout callback for fetchWithAuth to trigger logout on 401 after refresh failure
  useEffect(() => {
    // Create a stable logout function reference for the callback
    const handleAuthFailure = () => {
      console.log('[Auth] fetchWithAuth triggered logout due to auth failure');
      // Clear JWT token
      clearJwtToken();
      // Clear legacy auth
      clearAuthToken();
      setAuthenticated(false);
      setUser(null);
    };

    setLogoutCallback(handleAuthFailure);

    // Cleanup: unregister callback on unmount
    return () => {
      setLogoutCallback(null);
    };
  }, []); // Only run once on mount

  const login = (jwtToken, userData) => {
    setAuthenticated(true);

    // Store JWT token (NEW - modern authentication)
    if (jwtToken) {
      setJwtToken(jwtToken);
      console.log('[Auth] JWT token stored successfully');
    } else {
      console.warn('[Auth] No JWT token provided, falling back to legacy auth');
    }

    // Backward compatibility: Keep legacy auth token
    setAuthToken('true');

    // Always store user data, even if minimal
    const userToStore = userData || { Role: 'Host' };
    setUser(userToStore);
    storeUser(userToStore);
  };

  const logout = () => {
    // Clear JWT token
    clearJwtToken();
    console.log('[Auth] JWT token cleared on logout');

    // Clear legacy auth
    clearAuthToken();
    setAuthenticated(false);
    setUser(null);
  };

  const value = {
    authenticated,
    loading,
    user,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
